import {
  ORDER_HUB_API_BASE_URL,
  SESSION_BASE_URL,
} from "../lib/constants/urls";
import { callAPI } from "../utils/api";

import CSRFController from "./CSRFController";
import { Errors } from "../errors";
import Router from "next/router";

export interface UserInformation {
  uid: string;
  email: string;
  phoneNumber?: string;
}

class AuthController {
  async login(credentials: { email: string; password: string }) {
    const response = await fetch(`${SESSION_BASE_URL}/login`, {
      method: "POST",
      body: JSON.stringify({ ...credentials, type: "email" }),
      headers: {
        "x-csrf-token": await CSRFController.getOrFetch(),
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (response.status === 401) {
      throw new Error(Errors.LoginUnauthorized);
    } else if (response.status >= 500) {
      throw new Error(Errors.InternalServerError);
    } else if (!response.ok) {
      throw new Error(Errors.LoginLoadFailed);
    }

    const { user } = await response.json();
    return user;
  }

  async loginOTP(credentials: { phonenumber: string; otpCode: string }) {
    const response = await fetch(`${SESSION_BASE_URL}/login`, {
      method: "POST",
      body: JSON.stringify({ ...credentials, type: "phone" }),
      headers: {
        "x-csrf-token": await CSRFController.getOrFetch(),
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (response.status === 401) {
      throw new Error(Errors.LoginUnauthorized);
    } else if (response.status >= 500) {
      throw new Error(Errors.InternalServerError);
    } else if (!response.ok) {
      throw new Error(Errors.LoginLoadFailed);
    }

    const { user } = await response.json();
    return user;
  }

  async sendOTP(phoneNumber: string) {
    return await callAPI(["user", "send-otp-code"], {
      method: "POST",
      args: { phoneNumber },
      isAuthd: false,
    }).catch(() => {
      throw new Error(Errors.SendOTPFailed);
    });
  }

  async loginToken(credentials: { token: string }) {
    const response = await fetch(`${SESSION_BASE_URL}/login`, {
      method: "POST",
      body: JSON.stringify({ ...credentials, type: "token" }),
      headers: {
        "x-csrf-token": await CSRFController.getOrFetch(),
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (response.status === 401) {
      throw new Error(Errors.LoginUnauthorized);
    } else if (response.status >= 500) {
      throw new Error(Errors.InternalServerError);
    } else if (!response.ok) {
      throw new Error(Errors.LoginLoadFailed);
    }

    const responseData = await response.json();

    if ("redirectUrl" in responseData) {
      Router.push(responseData.redirectUrl);
    }

    const { user } = responseData;
    return user;
  }

  async changePassword(body: {
    oldPassword: string;
    password: string;
    passwordConfirm: string;
  }) {
    const response = await fetch(`${SESSION_BASE_URL}/change-password`, {
      method: "POST",
      headers: {
        "x-csrf-token": await CSRFController.getOrFetch(),
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(Errors.ChangePasswordFailed);
    }

    const responseData = await response.json();
    return responseData.data.email;
  }

  async requestEmailChange(body: {
    password: string;
    oldEmail: string;
    newEmail: string;
  }) {
    const response = await fetch(
      `${ORDER_HUB_API_BASE_URL}/user/email-change-request`,
      {
        method: "POST",
        headers: {
          "x-csrf-token": await CSRFController.getOrFetch(),
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(body),
      }
    );

    if (!response.ok) {
      throw new Error(Errors.ChangeEmailFailed);
    }

    return true;
  }

  async logout() {
    const response = await fetch(`${SESSION_BASE_URL}/logout`, {
      method: "POST",
      headers: {
        "x-csrf-token": await CSRFController.getOrFetch(),
      },
      credentials: "include",
    });

    if (response.status !== 401 && response.status !== 404 && !response.ok) {
      throw new Error(Errors.LogoutFailed);
    }
  }

  async testAuth(): Promise<UserInformation | null> {
    const response = await fetch(`${ORDER_HUB_API_BASE_URL}/test-auth`, {
      method: "GET",
      headers: {
        "x-csrf-token": await CSRFController.getOrFetch(),
      },
      credentials: "include",
    });

    if (response.status === 401 || response.status === 404) {
      return null;
    } else {
      return await response.json();
    }
  }
}

const authController = new AuthController();
export default authController;
